import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformApi } from './platformApi';
import { OnboardModal } from './PlatformTenantsPage';

interface PlatformCrmLead {
  leadId: string;
  businessName: string;
  contactName: string | null;
  phone: string | null;
  city: string | null;
  vertical: number | null;
  status: number;
  notes: string | null;
  convertedTenantId: string | null;
}

const STATUS_KEYS: Record<number, string> = {
  1: 'new',
  2: 'contacted',
  3: 'demo',
  4: 'negotiation',
  5: 'won',
  6: 'lost',
};
const STATUS_COLORS: Record<number, string> = {
  1: 'blue',
  2: 'cyan',
  3: 'grape',
  4: 'orange',
  5: 'green',
  6: 'gray',
};

export function PlatformCrmPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [editLead, setEditLead] = useState<PlatformCrmLead | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [convertLead, setConvertLead] = useState<PlatformCrmLead | null>(null);

  const { data: leads, isLoading } = useQuery({
    queryKey: ['platform-crm', filter] as const,
    queryFn: async () =>
      (
        await platformApi.get<PlatformCrmLead[]>('/api/platform/crm/leads', {
          params: filter === 'all' ? {} : { status: filter },
        })
      ).data,
  });

  const statusLabel = (s: number) => t(`platform.crm.status.${STATUS_KEYS[s] ?? 'new'}`);

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <SegmentedControl
          value={filter}
          onChange={setFilter}
          data={[
            { value: 'all', label: t('platform.crm.all') },
            ...Object.entries(STATUS_KEYS).map(([value, key]) => ({
              value,
              label: t(`platform.crm.status.${key}`),
            })),
          ]}
        />
        <Button
          color="wheels"
          leftSection={<IconPlus size={16} stroke={1.8} />}
          onClick={() => setCreateOpen(true)}
        >
          {t('platform.crm.add')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? (
          <Group justify="center" py="lg">
            <Loader color="wheelsRed" />
          </Group>
        ) : (leads ?? []).length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            {t('platform.crm.empty')}
          </Text>
        ) : (
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('platform.crm.business')}</Table.Th>
                <Table.Th>{t('platform.crm.contact')}</Table.Th>
                <Table.Th>{t('platform.crm.city')}</Table.Th>
                <Table.Th>{t('platform.crm.statusCol')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(leads ?? []).map((lead) => (
                <Table.Tr key={lead.leadId}>
                  <Table.Td>{lead.businessName}</Table.Td>
                  <Table.Td>
                    {lead.contactName}
                    {lead.phone && (
                      <Text size="xs" c="dimmed" dir="ltr">
                        {lead.phone}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{lead.city}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={STATUS_COLORS[lead.status]}>
                      {statusLabel(lead.status)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Button size="xs" variant="default" onClick={() => setEditLead(lead)}>
                        {t('common.edit')}
                      </Button>
                      {lead.status < 5 && (
                        <Button size="xs" color="green" onClick={() => setConvertLead(lead)}>
                          {t('platform.crm.convert')}
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <LeadModal
        opened={createOpen || editLead !== null}
        lead={editLead}
        onClose={() => {
          setCreateOpen(false);
          setEditLead(null);
        }}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ['platform-crm'] })}
      />
      <OnboardModal
        opened={convertLead !== null}
        onClose={() => setConvertLead(null)}
        leadId={convertLead?.leadId}
      />
    </Stack>
  );
}

function LeadModal({
  opened,
  lead,
  onClose,
  onSaved,
}: {
  opened: boolean;
  lead: PlatformCrmLead | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    businessName: '',
    contactName: '',
    phone: '',
    city: '',
    vertical: '1',
    status: '1',
    notes: '',
  });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Hydrate the form when opening for an existing lead.
  if (lead && loadedFor !== lead.leadId) {
    setForm({
      businessName: lead.businessName,
      contactName: lead.contactName ?? '',
      phone: lead.phone ?? '',
      city: lead.city ?? '',
      vertical: String(lead.vertical ?? 1),
      status: String(lead.status),
      notes: lead.notes ?? '',
    });
    setLoadedFor(lead.leadId);
  }
  if (!lead && loadedFor !== null) {
    setForm({ businessName: '', contactName: '', phone: '', city: '', vertical: '1', status: '1', notes: '' });
    setLoadedFor(null);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        businessName: form.businessName,
        contactName: form.contactName || null,
        phone: form.phone || null,
        email: null,
        city: form.city || null,
        vertical: Number(form.vertical),
        notes: form.notes || null,
      };
      if (lead) {
        return platformApi.put(`/api/platform/crm/leads/${lead.leadId}`, {
          ...payload,
          status: Number(form.status),
          assignedToPlatformUserId: null,
        });
      }
      return platformApi.post('/api/platform/crm/leads', payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={lead ? t('common.edit') : t('platform.crm.add')}>
      <Stack gap="sm">
        <TextInput
          label={t('platform.crm.business')}
          value={form.businessName}
          onChange={(e) => setForm((f) => ({ ...f, businessName: e.currentTarget.value }))}
          required
        />
        <Group grow>
          <TextInput
            label={t('platform.crm.contact')}
            value={form.contactName}
            onChange={(e) => setForm((f) => ({ ...f, contactName: e.currentTarget.value }))}
          />
          <TextInput
            label={t('platform.crm.phone')}
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.currentTarget.value }))}
            dir="ltr"
          />
        </Group>
        <Group grow>
          <TextInput
            label={t('platform.crm.city')}
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.currentTarget.value }))}
          />
          <Select
            label={t('platform.tenants.vertical')}
            value={form.vertical}
            onChange={(v) => setForm((f) => ({ ...f, vertical: v ?? '1' }))}
            data={[
              { value: '1', label: t('platform.verticals.qsr') },
              { value: '2', label: t('platform.verticals.restaurant') },
              { value: '3', label: t('platform.verticals.supermarket') },
            ]}
          />
        </Group>
        {lead && (
          <Select
            label={t('platform.crm.statusCol')}
            value={form.status}
            onChange={(v) => setForm((f) => ({ ...f, status: v ?? '1' }))}
            data={[1, 2, 3, 4, 6].map((s) => ({
              value: String(s),
              label: t(`platform.crm.status.${STATUS_KEYS[s]!}`),
            }))}
            description={t('platform.crm.wonHint')}
          />
        )}
        <Textarea
          label={t('platform.crm.notes')}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.currentTarget.value }))}
          autosize
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            color="wheels"
            loading={mutation.isPending}
            disabled={!form.businessName}
            onClick={() => mutation.mutate()}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
